import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DONE_STEP_ID, fetchHireHealthSurveyProgress, upsertHireHealthSurveyProgress } from './hireHealthSurveyApi';
import {
  emptyExamDraft,
  emptyTextDraft,
  examDraftFromValues,
  mergeValuesFromSnapshots,
  snapshotFromExam,
  snapshotFromText,
  textDraftFromValues,
} from './hireHealthSurveyEntity';
import { buildSectionSummary } from './hireHealthSurveySummary';
import {
  getCommittedStepsForSection,
  resumeStepForSectionSnapshot,
  stateAfterUndoLastCommitted,
} from './hireHealthSurveyDisplay';
import {
  firstStepForSection,
  inferCompletedSectionsFromStep,
  nextSectionAfter,
  nextStepAfter,
  sectionIdForStep,
  SECTION_ORDER,
} from './hireHealthSurveySteps';
import type {
  CompletedSectionEntry,
  ExamSectionDraft,
  ExamSectionId,
  HireHealthFormValues,
  HireHealthStep,
  SectionId,
  SectionSnapshot,
  TextSectionDraft,
  TextSectionId,
  YesNo,
} from './hireHealthSurveyTypes';

function isExamSection(id: SectionId): id is ExamSectionId {
  return id !== 'aftereffects' && id !== 'symptoms';
}

function snapshotForSection(sectionId: SectionId, values: HireHealthFormValues): SectionSnapshot {
  if (isExamSection(sectionId)) {
    const d = examDraftFromValues(sectionId, values);
    if (!d.hadExam) {
      return snapshotFromExam(sectionId, { hadExam: 'no', examDate: '', hospital: '', fit: '' });
    }
    return snapshotFromExam(sectionId, d);
  }
  const d = textDraftFromValues(sectionId, values);
  if (!d.hadIssue) {
    return snapshotFromText(sectionId, { hadIssue: 'no', detail: '' });
  }
  return snapshotFromText(sectionId, d);
}

function collectMaps(
  completed: CompletedSectionEntry[],
  examInProgress: ExamSectionDraft | null,
  textInProgress: TextSectionDraft | null,
  activeSection: SectionId | null
) {
  const exams: Partial<Record<ExamSectionId, ExamSectionDraft>> = {};
  const texts: Partial<Record<TextSectionId, TextSectionDraft>> = {};
  completed.forEach((c) => {
    if (c.snapshot.kind === 'exam') exams[c.snapshot.sectionId] = c.snapshot.data;
    else texts[c.snapshot.sectionId] = c.snapshot.data;
  });
  if (activeSection && isExamSection(activeSection) && examInProgress) {
    exams[activeSection] = examInProgress;
  }
  if (activeSection && !isExamSection(activeSection) && textInProgress) {
    texts[activeSection] = textInProgress;
  }
  return { exams, texts };
}

export function useHireHealthSurveyFlow(
  currentEmail: string | null | undefined,
  onComplete: () => void,
  t: (key: string) => string
) {
  const [step, setStep] = useState<HireHealthStep>('general_gate');
  const [completedSections, setCompletedSections] = useState<CompletedSectionEntry[]>([]);
  const [examDraft, setExamDraft] = useState<ExamSectionDraft>(emptyExamDraft());
  const [textDraft, setTextDraft] = useState<TextSectionDraft>(emptyTextDraft());
  const [expandedSectionIndex, setExpandedSectionIndex] = useState<number | null>(null);
  const [editReturnStep, setEditReturnStep] = useState<HireHealthStep | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const progressLoadedRef = useRef(false);
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const activeSection = sectionIdForStep(step);
  const inProgressVisible = expandedSectionIndex === null && step !== 'finalConfirm';

  const persist = useCallback(
    async (nextStep: HireHealthStep, values: HireHealthFormValues) => {
      if (!currentEmail?.trim()) return;
      await upsertHireHealthSurveyProgress({
        email: currentEmail,
        values,
        currentStepId: nextStep,
      });
    },
    [currentEmail]
  );

  const buildAllValues = useCallback(
    (
      completed: CompletedSectionEntry[],
      ex: ExamSectionDraft | null,
      tx: TextSectionDraft | null,
      active: SectionId | null,
      dateConfirmed?: string
    ) => {
      const { exams, texts } = collectMaps(completed, ex, tx, active);
      return mergeValuesFromSnapshots(exams, texts, dateConfirmed);
    },
    []
  );

  const finishSection = useCallback(
    async (sectionId: SectionId, snapshot: SectionSnapshot) => {
      const text = buildSectionSummary(snapshot, t);
      const entry: CompletedSectionEntry = { sectionId, text, snapshot };
      const nextSection = nextSectionAfter(sectionId);

      setCompletedSections((prev) => {
        const nextList = [...prev.filter((p) => p.sectionId !== sectionId), entry];
        if (nextSection === 'finalConfirm') {
          void persist('finalConfirm', buildAllValues(nextList, null, null, null));
        } else {
          const nextStep = firstStepForSection(nextSection);
          void persist(nextStep, buildAllValues(nextList, null, null, null));
        }
        return nextList;
      });

      if (nextSection === 'finalConfirm') {
        setStep('finalConfirm');
        setExamDraft(emptyExamDraft());
        setTextDraft(emptyTextDraft());
        return;
      }
      setStep(firstStepForSection(nextSection));
      setExamDraft(emptyExamDraft());
      setTextDraft(emptyTextDraft());
    },
    [buildAllValues, persist, t]
  );

  const maybeReturnAfterEdit = useCallback(
    (snapshot: SectionSnapshot) => {
      if (editReturnStep !== 'finalConfirm' || expandedSectionIndex === null) return false;
      const idx = expandedSectionIndex;
      const sectionId = snapshot.kind === 'exam' ? snapshot.sectionId : snapshot.sectionId;
      const text = buildSectionSummary(snapshot, t);
      const entry: CompletedSectionEntry = { sectionId, text, snapshot };
      setCompletedSections((prev) => {
        const next = [...prev];
        next[idx] = entry;
        return next;
      });
      if (snapshot.kind === 'exam') {
        setExamDraft({ ...snapshot.data });
        setTextDraft(emptyTextDraft());
      } else {
        setTextDraft({ ...snapshot.data });
        setExamDraft(emptyExamDraft());
      }
      setStep('finalConfirm');
      const values = buildAllValues(
        completedSections.map((c, i) => (i === idx ? entry : c)),
        null,
        null,
        null
      );
      void persist('finalConfirm', values);
      return true;
    },
    [editReturnStep, expandedSectionIndex, buildAllValues, completedSections, persist, t]
  );

  const advance = useCallback(
    async (overrides?: { examDraft?: ExamSectionDraft; textDraft?: TextSectionDraft }) => {
      if (!activeSection) return;

      const ex = overrides?.examDraft ?? examDraft;
      const tx = overrides?.textDraft ?? textDraft;

      const snap = isExamSection(activeSection)
        ? snapshotFromExam(activeSection, ex)
        : snapshotFromText(activeSection, tx);

      if (maybeReturnAfterEdit(snap)) {
        return;
      }

      const hadYes = step.endsWith('_gate')
        ? isExamSection(activeSection)
          ? ex.hadExam === 'yes'
          : tx.hadIssue === 'yes'
        : true;

      const next = nextStepAfter(step, hadYes);
      if (next === 'finishSection') {
        await finishSection(activeSection, snap);
        return;
      }

      setStep(next);
      const values = buildAllValues(completedSections, ex, tx, activeSection);
      void persist(next, values);
    },
    [
      step,
      examDraft,
      textDraft,
      activeSection,
      maybeReturnAfterEdit,
      finishSection,
      buildAllValues,
      completedSections,
      persist,
    ]
  );

  const handleGateYesNo = useCallback(
    async (v: YesNo) => {
      if (!activeSection) return;
      const editingFromReview = editReturnStep === 'finalConfirm' && expandedSectionIndex !== null;
      if (isExamSection(activeSection)) {
        const draft: ExamSectionDraft = { ...examDraft, hadExam: v };
        setExamDraft(draft);
        const snap = snapshotFromExam(activeSection, draft);
        if (editingFromReview) {
          if (v === 'no') {
            if (maybeReturnAfterEdit(snap)) return;
          } else {
            setStep(`${activeSection}_date` as HireHealthStep);
            return;
          }
        }
        if (v === 'no') {
          await finishSection(activeSection, snap);
          return;
        }
        const next = nextStepAfter(step, true);
        if (typeof next === 'string' && next !== 'finishSection') {
          setStep(next);
          void persist(next, buildAllValues(completedSections, draft, textDraft, activeSection));
        }
      } else {
        const draft: TextSectionDraft = { ...textDraft, hadIssue: v };
        setTextDraft(draft);
        const snap = snapshotFromText(activeSection, draft);
        if (editingFromReview) {
          if (v === 'no') {
            if (maybeReturnAfterEdit(snap)) return;
          } else {
            setStep(`${activeSection}_detail` as HireHealthStep);
            return;
          }
        }
        if (v === 'no') {
          await finishSection(activeSection, snap);
          return;
        }
        const next = nextStepAfter(step, true);
        if (typeof next === 'string' && next !== 'finishSection') {
          setStep(next);
          void persist(next, buildAllValues(completedSections, examDraft, draft, activeSection));
        }
      }
    },
    [
      activeSection,
      examDraft,
      textDraft,
      step,
      editReturnStep,
      expandedSectionIndex,
      maybeReturnAfterEdit,
      finishSection,
      buildAllValues,
      completedSections,
      persist,
    ]
  );

  const handleFit = useCallback(
    (fit: 'fit' | 'not_fit') => {
      const draft = { ...examDraft, fit };
      setExamDraft(draft);
      void advance({ examDraft: draft });
    },
    [advance, examDraft]
  );

  const handleFinalComplete = useCallback(async (): Promise<boolean> => {
    if (!currentEmail?.trim()) {
      onComplete();
      return true;
    }
    const today = new Date().toISOString().slice(0, 10);
    const values = buildAllValues(completedSections, null, null, null, today);
    try {
      await upsertHireHealthSurveyProgress({
        email: currentEmail,
        values,
        currentStepId: DONE_STEP_ID,
      });
      onComplete();
      return true;
    } catch {
      setSaveError(t('submitDocuments.hireHealthSurvey.saveFailed'));
      return false;
    }
  }, [currentEmail, completedSections, buildAllValues, onComplete, t]);

  const handleEditSection = useCallback(
    (index: number) => {
      const entry = completedSections[index];
      if (!entry) return;
      setExpandedSectionIndex(index);
      setEditReturnStep('finalConfirm');
      if (entry.snapshot.kind === 'exam') {
        setExamDraft({ ...entry.snapshot.data });
        setTextDraft(emptyTextDraft());
      } else {
        setTextDraft({ ...entry.snapshot.data });
        setExamDraft(emptyExamDraft());
      }
      // Stay on final review (family-info pattern); composer opens only after Edit on a field.
      setStep('finalConfirm');
    },
    [completedSections]
  );

  const collapseEditReview = useCallback(() => {
    setExpandedSectionIndex(null);
    setEditReturnStep(null);
    setExamDraft(emptyExamDraft());
    setTextDraft(emptyTextDraft());
    setStep('finalConfirm');
  }, []);

  const showEditOnSummary = useCallback(
    (index: number) => {
      if (expandedSectionIndex !== null) return false;
      if (step !== 'finalConfirm') return false;
      return index >= 0 && index < completedSections.length;
    },
    [step, completedSections.length, expandedSectionIndex]
  );

  const showUndoOnSummary = useCallback(
    (index: number) => {
      if (expandedSectionIndex !== null) return false;
      if (step === 'finalConfirm') return false;
      return index === completedSections.length - 1;
    },
    [step, completedSections.length, expandedSectionIndex]
  );

  const beginEditField = useCallback(
    (fieldStep: HireHealthStep) => {
      if (expandedSectionIndex === null) return;
      setEditReturnStep('finalConfirm');
      setStep(fieldStep);
    },
    [expandedSectionIndex]
  );

  const handleUndoSection = useCallback(
    (index: number) => {
      const entry = completedSections[index];
      if (!entry) return;
      const sid = entry.sectionId;
      const ex =
        entry.snapshot.kind === 'exam' ? { ...entry.snapshot.data } : emptyExamDraft();
      const tx =
        entry.snapshot.kind === 'text' ? { ...entry.snapshot.data } : emptyTextDraft();
      const resumeStep = resumeStepForSectionSnapshot(sid, ex, tx);

      setCompletedSections((prev) => prev.slice(0, index));
      setStep(resumeStep);
      setExamDraft(ex);
      setTextDraft(tx);
      const values = buildAllValues(completedSections.slice(0, index), ex, tx, sid);
      void persist(resumeStep, values);
    },
    [completedSections, buildAllValues, persist]
  );

  useEffect(() => {
    if (!currentEmail || progressLoadedRef.current) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const data = await fetchHireHealthSurveyProgress(currentEmail);
        if (cancelled || !data) return;
        progressLoadedRef.current = true;
        const values = data.values ?? {};
        let st: HireHealthStep = 'general_gate';
        if (data.currentStepId === DONE_STEP_ID) {
          st = 'finalConfirm';
        } else if (data.currentStepId) {
          st = data.currentStepId as HireHealthStep;
        }
        const doneSectionIds =
          st === 'finalConfirm'
            ? [...SECTION_ORDER]
            : inferCompletedSectionsFromStep(st);
        const rebuilt: CompletedSectionEntry[] = doneSectionIds.map((sid) => {
          const snapshot = snapshotForSection(sid, values);
          return {
            sectionId: sid,
            text: buildSectionSummary(snapshot, t),
            snapshot,
          };
        });
        setCompletedSections(rebuilt);
        setStep(st);
        if (st !== 'finalConfirm' && sectionIdForStep(st)) {
          const sid = sectionIdForStep(st)!;
          if (isExamSection(sid)) {
            setExamDraft(examDraftFromValues(sid, values));
          } else {
            setTextDraft(textDraftFromValues(sid, values));
          }
        }
      } catch {
        progressLoadedRef.current = true;
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentEmail, t]);

  const committedStepsInProgress = useMemo(() => {
    if (!inProgressVisible || !activeSection) return [];
    return getCommittedStepsForSection(activeSection, step, examDraft, textDraft);
  }, [inProgressVisible, activeSection, step, examDraft, textDraft]);

  const lastCommittedStepInProgress = committedStepsInProgress[committedStepsInProgress.length - 1];

  const showCurrentStepPrompt = useMemo(() => {
    if (!inProgressVisible || !activeSection) return false;
    return !committedStepsInProgress.includes(step);
  }, [inProgressVisible, activeSection, step, committedStepsInProgress]);

  const handleUndoInProgress = useCallback(() => {
    if (!activeSection || !lastCommittedStepInProgress) return;
    const next = stateAfterUndoLastCommitted(
      activeSection,
      lastCommittedStepInProgress,
      examDraft,
      textDraft
    );
    setStep(next.step);
    setExamDraft(next.examDraft);
    setTextDraft(next.textDraft);
    void persist(next.step, buildAllValues(completedSections, next.examDraft, next.textDraft, activeSection));
  }, [
    activeSection,
    lastCommittedStepInProgress,
    completedSections,
    examDraft,
    textDraft,
    buildAllValues,
    persist,
  ]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [completedSections, step, expandedSectionIndex, examDraft, textDraft, committedStepsInProgress]);

  return {
    t,
    step,
    completedSections,
    examDraft,
    textDraft,
    activeSection,
    expandedSectionIndex,
    inProgressVisible,
    showStepPrompt: showCurrentStepPrompt,
    committedStepsInProgress,
    lastCommittedStepInProgress,
    handleUndoInProgress,
    setExamDraft,
    setTextDraft,
    advance,
    handleGateYesNo,
    handleFit,
    handleFinalComplete,
    handleEditSection,
    beginEditField,
    collapseEditReview,
    showEditOnSummary,
    showUndoOnSummary,
    handleUndoSection,
    threadEndRef,
    isLoading,
    saveError,
  };
}
